import { Link } from "react-router-dom";
import { Info } from "lucide-react";

/**
 * SubBandChip — inline chip that surfaces the sub-band position within
 * a bucket, e.g. "high in Target · 0.20 to Likely" or "well below the
 * Ambitious cutoff". Keeps the numeric gap score honest without
 * dominating the row header.
 *
 * Renders as a small pill; clicking (via the info icon) leads to the
 * chance-score explainer on the Methodology page so families can see
 * exactly how the numbers translate to buckets.
 */
export function SubBandChip({ classification, rowId }) {
  if (!classification?.sub_band) return null;
  const { bucket, sub_band } = classification;
  const { edge, distance_to_next_up, distance_into_band } = sub_band;

  const bucketLabel = {
    likely: "Likely",
    target: "Target",
    ambitious: "Ambitious",
    highly_ambitious: "Highly Ambitious",
  }[bucket] || "Target";

  // Human-readable position: "well inside" / "middle of" / "just inside"
  // / "well below" — computed off the edge + distance so the copy is
  // always congruent with the underlying numeric gap.
  const position = (() => {
    if (bucket === "highly_ambitious") {
      return edge === "low" ? "well below the Ambitious cutoff" : "just below the Ambitious cutoff";
    }
    if (bucket === "likely") {
      return edge === "high" ? "well inside Likely" : "just inside Likely";
    }
    if (edge === "high") return `high in ${bucketLabel}`;
    if (edge === "low") return `just inside ${bucketLabel}`;
    return `mid-${bucketLabel}`;
  })();

  const nextUpLabel = {
    target: "Likely",
    ambitious: "Target",
    highly_ambitious: "Ambitious",
  }[bucket];

  const detail = distance_to_next_up != null && nextUpLabel
    ? `${distance_to_next_up.toFixed(2)} to ${nextUpLabel}`
    : distance_into_band != null
      ? `+${distance_into_band.toFixed(2)} into band`
      : null;

  return (
    <span
      data-testid={`sub-band-chip-${rowId}`}
      className="inline-flex items-center gap-1 rounded-full border border-stone-300 bg-stone-50 px-2 py-0.5 text-[10px] font-underground-sc tracking-wider text-stone-700 normal-case"
      title={`Rule-based score sub-band. See methodology for how thresholds map to buckets.`}
    >
      {position}
      {detail && <span className="text-stone-500">· {detail}</span>}
      <Link
        to="/methodology#chance-score"
        data-testid={`sub-band-explain-${rowId}`}
        className="text-stone-400 hover:text-[#4C1D95]"
        aria-label="How the gap score is calculated"
        onClick={(e) => e.stopPropagation()}
      >
        <Info className="h-3 w-3" />
      </Link>
    </span>
  );
}

/**
 * ConfidenceTierChip — shown only when the classifier can't back its
 * bucket with a firm quantitative signal. Two levels:
 *   * partial     — one input translated / missing, treat cautiously
 *   * indicative  — genuinely sparse data (typically non-UK/US unis
 *                    with no published entry band, or a grade that
 *                    couldn't be mapped at all).
 *
 * The pill acts as an escape valve: it tells the family "this is our
 * best guess, not a probability" and links to the methodology so they
 * can see why. This preserves trust as we expand beyond the well-
 * documented UK / US / AU catalogue.
 */
export function ConfidenceTierChip({ tier, rowId }) {
  if (!tier?.tier || tier.tier === "quantitative") return null;
  const label = tier.tier === "indicative" ? "Indicative fit — limited data" : "Partial data";
  const cls = tier.tier === "indicative"
    ? "border-amber-300 bg-amber-50 text-amber-900"
    : "border-sky-300 bg-sky-50 text-sky-900";
  const reason = (tier.reasons || [])[0];
  return (
    <span
      data-testid={`confidence-tier-chip-${tier.tier}-${rowId}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-underground-sc tracking-wider normal-case ${cls}`}
      title={reason || "Read the methodology for how this is calculated."}
    >
      {label}
      <Link
        to="/methodology#chance-score"
        data-testid={`confidence-tier-explain-${tier.tier}-${rowId}`}
        className="text-current opacity-70 hover:opacity-100"
        aria-label="Why is this labelled Indicative or Partial?"
        onClick={(e) => e.stopPropagation()}
      >
        <Info className="h-3 w-3" />
      </Link>
    </span>
  );
}

export default SubBandChip;
